import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Instagram,
  Facebook,
  Music2,
  Linkedin,
  Youtube,
  AtSign,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';

/**
 * RotatingQuote — single line of social proof that cycles every 5s so
 * first-time visitors immediately see what real people say about the app
 * without burning vertical space on a fixed testimonial block.
 */
const RotatingQuote = ({ isSpanish }) => {
  const quotes = isSpanish
    ? [
        '"El temporizador de enfoque fue mi favorito! Me hizo sentir muy tranquila." — Tamsira N.',
        '"Es como una versión profesional del diccionario urbano." — Matt D.',
        'Hecho por una mamá neurodivergente de 4 niños neurodivergentes 💙',
      ]
    : [
        '"The Focus timer was my favourite! It made me feel very calm." — Tamsira N.',
        '"It\'s like a professional version of urban dictionary." — Matt D.',
        'Built by a neurodivergent mom of 4 neurodivergent kids 💙',
      ];
  const [i, setI] = useState(() => Math.floor(Math.random() * quotes.length));
  useEffect(() => {
    const id = setInterval(() => setI((x) => (x + 1) % quotes.length), 5000);
    return () => clearInterval(id);
  }, [quotes.length]);
  return (
    <p
      key={i}
      data-testid="linktree-rotating-quote"
      className="mb-3 text-xs sm:text-sm text-center text-muted-foreground italic px-2 animate-in fade-in duration-700"
    >
      {quotes[i]}
    </p>
  );
};



/**
 * Linktree-style hub that sits at the top of the homepage.
 *
 * Kept deliberately spartan: large avatar, wordmark, tagline, vertical stack
 * of primary CTAs, social row. The rest of the homepage (Phrase of the Day,
 * QuickStats, dialect selector, category grid) still renders below as
 * secondary discovery content.
 *
 * All icons come from lucide-react — no emojis, no dinosaur references other
 * than the official pixel-art avatar.
 */

// Per user rule (May 2026): the linktree CTA pills MUST be in
// alphabetical order. Add new pills here in the right alphabetic
// slot — do NOT just append to the end. Friends slots between
// "For Organizations" and "Phrase Library" alphabetically, and only
// renders for logged-in users.
const LINKS = (t, isSpanish, isAuthenticated) => {
  const core = [
    {
      id: 'blog',
      to: '/blog',
      label: isSpanish ? 'Blog' : 'Blog',
      bg: 'bg-[#F97316] hover:bg-[#EA580C]', // orange
    },
    {
      id: 'emojis',
      to: '/communication-emojis',
      // Alphabetic slot between "Blog" and "Dino Llama Fun Timer" (B → C → D).
      label: isSpanish ? 'Emojis de Comunicación' : 'Communication Emojis',
      bg: 'bg-[#D946EF] hover:bg-[#C026D3]', // magenta — user-requested
    },
    {
      id: 'timer',
      to: '/timer',
      label: isSpanish ? 'Temporizador Dino Llama' : 'Dino Llama Fun Timer',
      bg: 'bg-[#EC4899] hover:bg-[#DB2777]', // pink
    },
    {
      id: 'partners',
      to: '/for-organizations',
      label: isSpanish ? 'Para Organizaciones' : 'For Organizations',
      bg: 'bg-[#475569] hover:bg-[#334155]', // slate
    },
    // Friends — alphabetical slot for authenticated users.
    ...(isAuthenticated ? [{
      id: 'friends',
      to: '/friends',
      label: isSpanish ? 'Amigos' : 'Friends',
      bg: 'bg-[#3B82F6] hover:bg-[#2563EB]', // blue
    }] : []),
    {
      id: 'library',
      to: '/library',
      label: isSpanish ? 'Biblioteca de Frases' : 'Phrase Library',
      bg: 'bg-[#00C9B3] hover:bg-[#00B89F]', // brand teal
    },
    {
      id: 'resources',
      to: '/resources',
      label: isSpanish ? 'Recursos' : 'Resources',
      bg: 'bg-[#00A896] hover:bg-[#008B7C]', // deep teal
    },
    {
      id: 'interpreter',
      to: '/interpreter',
      label: isSpanish ? 'Intérprete de Pensamientos' : 'Thought Interpreter',
      bg: 'bg-[#8B5CF6] hover:bg-[#7C3AED]', // purple
    },
    {
      id: 'todo',
      to: '/todo',
      label: isSpanish ? 'Lista de Tareas' : 'To-Do List',
      bg: 'bg-[#10B981] hover:bg-[#059669]', // emerald
    },
  ];
  // Defensive sort by English label so the rule survives future label edits.
  // Uses the English label even in Spanish mode so the order is identical
  // across locales — this matches the "alphabetical" UX promise.
  const ENG_LABEL = {
    blog: 'Blog',
    emojis: 'Communication Emojis',
    timer: 'Dino Llama Fun Timer',
    partners: 'For Organizations',
    friends: 'Friends',
    library: 'Phrase Library',
    resources: 'Resources',
    interpreter: 'Thought Interpreter',
    todo: 'To-Do List',
  };
  return core.sort((a, b) =>
    (ENG_LABEL[a.id] || a.label).localeCompare(ENG_LABEL[b.id] || b.label)
  );
};

// Per user rule (May 2026): social links MUST be alphabetical.
// Add new socials in their alphabetic slot — do NOT just append.
const SOCIALS = [
  {
    id: 'facebook',
    href: 'https://www.facebook.com/clearcommunicationapp',
    icon: Facebook,
    label: 'Facebook',
    brand: 'hover:bg-[#1877F2] hover:text-white',
  },
  {
    id: 'instagram',
    href: 'https://www.instagram.com/clearcommunicationapp',
    icon: Instagram,
    label: 'Instagram',
    brand: 'hover:bg-gradient-to-br hover:from-[#fd5949] hover:via-[#d6249f] hover:to-[#285AEB] hover:text-white',
  },
  {
    id: 'linkedin',
    href: 'https://www.linkedin.com/company/clearcommunicationapp',
    icon: Linkedin,
    label: 'LinkedIn',
    brand: 'hover:bg-[#0A66C2] hover:text-white',
  },
  {
    id: 'threads',
    href: 'https://www.threads.com/@ClearCommunicationApp',
    icon: AtSign,
    label: 'Threads',
    brand: 'hover:bg-black hover:text-white',
  },
  {
    id: 'tiktok',
    href: 'https://www.tiktok.com/@clearcommunicationapp',
    icon: Music2,
    label: 'TikTok',
    brand: 'hover:bg-black hover:text-white',
  },
  {
    id: 'youtube',
    href: 'https://www.youtube.com/@ClearCommunicationApp',
    icon: Youtube,
    label: 'YouTube',
    brand: 'hover:bg-[#FF0000] hover:text-white',
  },
];

export const LinktreeHub = () => {
  const navigate = useNavigate();
  const { t, isSpanish } = useLanguage();
  const { isAuthenticated } = useAuth();

  const links = LINKS(t, isSpanish, isAuthenticated);

  return (
    <section
      className="pt-3 pb-4 px-4"
      data-testid="linktree-hub"
      aria-label="ClearCommunicationApp main links"
    >
      {/* On desktop we let the layout expand to two columns of pills
          (~max-w-3xl) so the page doesn't have huge empty rails on
          1080p+ monitors. Mobile keeps a single column for thumb reach. */}
      <div className="max-w-md md:max-w-3xl mx-auto flex flex-col items-center">
        {/* Social-proof rotator — single line above the avatar. */}
        <RotatingQuote isSpanish={isSpanish} />

        {/* Centered avatar — keeps the original "Detective Dino with
            magnifying glass + ? thought bubble" identity per user
            feedback May 30, 2026. The take-a-tour CTA lives in its
            own fixed-position component in the upper-left corner. */}
        <div
          className="h-24 w-24 flex items-center justify-center mb-2"
          data-testid="linktree-avatar-wrap"
        >
          <img
            src="/dino-clean.png"
            alt="ClearCommunicationApp"
            width={96}
            height={96}
            className="h-24 w-24"
            style={{ imageRendering: 'pixelated' }}
            data-testid="linktree-avatar"
          />
        </div>

        {/* Wordmark + tagline — handle links to the About page so
            visitors can quickly learn the mission/team. */}
        <h1 className="mt-4 text-2xl font-bold tracking-tight text-center">
          <button
            onClick={() => navigate('/about')}
            className="hover:text-[#00A896] focus:outline-none focus:underline transition-colors"
            data-testid="linktree-handle-link"
            aria-label="About ClearCommunicationApp"
          >
            @clearcommunicationapp
          </button>
        </h1>
        <p className="text-sm text-muted-foreground text-center mt-1">
          {isSpanish
            ? 'Claridad en la comunicación · Una herramienta de apoyo'
            : 'Reach clarity in communication · A supportive tool'}
        </p>

        {/* Credibility signals row */}
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-xs">
          <span className="px-2 py-0.5 rounded-full bg-[#00E5CC]/10 text-[#00A896] border border-[#00E5CC]/25">
            {isSpanish ? 'Hecho con neurodivergencia en mente' : 'Built with neurodivergence in mind'}
          </span>
          <span className="px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-700 border border-purple-500/25">
            3,400+ {isSpanish ? 'frases' : 'phrases'}
          </span>
          <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/25">
            EN · ES
          </span>
        </div>

        {/* CTA stack — single tight column. Icons + descriptions removed
            per user request so the linktree feels light and scannable.
            Each pill is a vibrant solid colour with bold centred text. */}
        <div className="mt-6 w-full grid grid-cols-1 gap-2.5">
          {links.map((link) => (
            <button
              key={link.id}
              onClick={() => navigate(link.to)}
              data-testid={`linktree-${link.id}`}
              className={`w-full rounded-xl text-white font-bold text-base sm:text-lg leading-none tracking-tight px-4 py-3.5 shadow-md hover:shadow-lg active:scale-[0.98] transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background focus:ring-white/40 ${link.bg}`}
            >
              {link.label}
            </button>
          ))}
        </div>

        {/* Google Review CTA — styled like the official Google badge:
            white square with the multicoloured "G" on the left, solid
            Google blue with white text on the right.

            We use the direct `search.google.com/local/writereview`
            URL (the resolved destination of `g.page/r/.../review`) so
            Opera, Samsung Internet, and other browsers that don't
            follow short-link redirects in target="_blank" tabs don't
            land on about:blank. */}
        <a
          href="https://search.google.com/local/writereview?placeid=ChIJZdO2X5D6tywRlRF6auFqeOg"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            // Inside the WebToNative app shell, native APIs are needed
            // to escape the in-app browser otherwise the link opens
            // a blank tab.
            try {
              const inApp = (typeof window !== 'undefined') &&
                (window.WTN || window.AppInterface);
              const targetUrl = 'https://search.google.com/local/writereview?placeid=ChIJZdO2X5D6tywRlRF6auFqeOg';
              if (window.WTN && typeof window.WTN.openInExternalBrowser === 'function') {
                e.preventDefault();
                window.WTN.openInExternalBrowser({ url: targetUrl });
              } else if (window.AppInterface && typeof window.AppInterface.openInExternalBrowser === 'function') {
                e.preventDefault();
                window.AppInterface.openInExternalBrowser(targetUrl);
              }
            } catch { /* fall through to default anchor behavior */ }
          }}
          data-testid="linktree-google-review-cta"
          className="mt-4 w-full max-w-md flex rounded-xl overflow-hidden shadow-md hover:shadow-lg active:scale-[0.99] transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4285F4]"
        >
          <span className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 bg-white flex items-center justify-center" aria-hidden="true">
            <svg viewBox="0 0 48 48" className="h-7 w-7 sm:h-8 sm:w-8">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
          </span>
          <span className="flex-1 bg-[#4285F4] hover:bg-[#3367D6] text-white font-semibold text-sm sm:text-base px-4 py-3 flex items-center justify-center text-center leading-snug">
            {isSpanish
              ? 'Haz clic aquí para dejarnos una reseña en Google'
              : 'Click here to leave us a review on Google'}
          </span>
        </a>

        {/* Support us — donation callout. Lives BELOW the
            Google Review badge so it doesn't disrupt the alphabetical
            pill list above, and uses a warm forest-green palette so it
            reads as "appreciation" rather than "buy now". */}
        <button
          onClick={() => navigate('/support')}
          data-testid="linktree-support"
          className="mt-4 w-full max-w-md rounded-xl bg-gradient-to-r from-[#15803d] via-[#16a34a] to-[#65a30d] text-white shadow-md hover:shadow-lg active:scale-[0.99] transition-all px-4 py-3 flex items-center justify-center gap-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#16a34a]"
        >
          <span aria-hidden="true" className="text-lg">🌳⭐</span>
          <span>
            {isSpanish
              ? '¿Disfrutas la app? Apóyanos'
              : 'Enjoy the app? Support us'}
          </span>
        </button>

        {/* Follow-us section — labeled link list (NOT icon buttons), all
            in one block per user request. Easier to scan, more accessible. */}
        <div
          className="mt-8 w-full rounded-2xl border bg-card/80 backdrop-blur-sm p-4 sm:p-5"
          data-testid="linktree-follow-section"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold text-center mb-1">
            {isSpanish ? 'Síguenos en redes sociales' : 'Follow us on social media'}
          </p>
          <p className="text-xs text-muted-foreground text-center mb-3">
            {isSpanish
              ? 'Nuevas frases, consejos y actualizaciones cada semana'
              : 'New phrases, tips, and updates every week'}
          </p>
          <ul
            className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm"
            data-testid="linktree-socials"
          >
            {SOCIALS.map((s, idx) => {
              const Icon = s.icon;
              return (
                <li key={s.id} className="flex items-center">
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    data-testid={`linktree-social-${s.id}`}
                    className="inline-flex items-center gap-1.5 font-medium text-foreground hover:text-[#00A896] transition-colors underline-offset-4 hover:underline"
                  >
                    <Icon className="h-4 w-4 opacity-70" />
                    {s.label}
                  </a>
                  {idx < SOCIALS.length - 1 && (
                    <span className="ml-4 text-muted-foreground" aria-hidden="true">·</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default LinktreeHub;
